import dayjs from 'dayjs';
import { cloneDeep, isNil, lowerCase } from 'lodash-es';
import { GeoTypeEnum } from '@/components/ReMap/types.d';
import { getDeptList } from '@/api/system/organization';

/**
 * 构造树型结构数据
 * @param {*} data 数据源
 * @param {*} id id字段 默认 'id'
 * @param {*} parentId 父节点字段 默认 'parentId'
 * @param {*} children 孩子节点字段 默认 'children'
 */
// eslint-disable-next-line max-params
export function handleTree(data: any, id = 'id', parentId = 'parentId', children = 'children') {
  let config = { id, parentId, childrenList: children };

  let childrenListMap: any = {};
  let nodeIds: any = {};
  let tree = [];

  for (let d of data) {
    let parentId = d[config.parentId];
    if (!childrenListMap[parentId]) {
      childrenListMap[parentId] = [];
    }
    nodeIds[d[config.id]] = d;
    childrenListMap[parentId].push(d);
  }

  for (let d of data) {
    let parentId = d[config.parentId];
    if (!nodeIds[parentId]) {
      tree.push(d);
    }
  }

  for (let t of tree) {
    adaptToChildrenList(t);
  }

  function adaptToChildrenList(o: any) {
    if (childrenListMap[o[config.id]]) {
      o[config.childrenList] = childrenListMap[o[config.id]];
    }
    if (o[config.childrenList]) {
      for (let c of o[config.childrenList]) {
        adaptToChildrenList(c);
      }
    }
  }
  return tree;
}

export function flatTree(tree: any[], childKey = 'children') {
  return tree.reduce((val, item) => {
    let flatArr = [...val, item];
    if (item[childKey]) {
      flatArr = [...flatArr, ...flatTree(item[childKey], childKey)];
    }
    return flatArr;
  }, []);
}

/**
 * 利用a标签下载文件
 * @param { BlobPart } data 文件数据流
 * @param { String } filename 文件名 （带有后缀，能决定文件的类型）
 */
export const downLoadFile = (data: BlobPart, filename: string) => {
  const blob = new Blob([data]);
  const downloadUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = downloadUrl;
  anchor.download = filename;
  anchor.click();
  window.URL.revokeObjectURL(downloadUrl);
};

/**
 * 自定义空字符串检验方法
 * @param rule
 * @param value
 * @param callback
 */
export const emptyStringValidator = (_rule: any, value: string, callback: (arg0?: Error | undefined) => void) => {
  if (/\s/.test(value)) {
    callback(new Error('值不能包含空格！'));
  } else {
    callback();
  }
};

/**
 * 自定义表单搜索项校验方法
 * @param searchModel
 */
export const emptySearchModelValidator = (searchModel: any) => {
  return Object.keys(searchModel).every((key) => {
    return searchModel[key] === null || searchModel[key] === undefined || searchModel[key] === '';
  });
};

/**
 * 下载文件统一文件名格式
 * @param formModel
 */
export const downloadFileNameFormat = (fileName: any) => {
  return `${dayjs().format('YYYY-MM-DD')}-${fileName}`;
};

/**
 * 计算一组经纬度的中心点
 * @param points
 */
export const getCenterPoint = (points: any) => {
  let sumX = 0;
  let sumY = 0;
  for (const point of points) {
    sumX += point[0];
    sumY += point[1];
  }
  let centerX = sumX / points.length;
  let centerY = sumY / points.length;
  return [centerX, centerY];
};

/**
 * 表单检验数字正数
 * @param _rule
 * @param value
 * @param callback
 */
export const validatePositiveNumber = (_rule: any, value: string, callback: (arg?: Error | undefined) => void) => {
  if (value === '' || value === null) {
    callback();
  } else {
    if (isNaN(Number(value))) {
      callback(new Error('请输入合法数字'));
    } else if (Number(value) <= 0) {
      callback(new Error('请输入正数'));
    }
    callback();
  }
};

/**
 * 验证电话号码（手机号或座机号）
 * @param rule
 * @param value
 * @param callback
 */
export const validatePhone = (_rule: any, value: string, callback: any) => {
  if (!value) {
    callback();
  }

  // 手机号正则：1开头，后面跟10位数字
  const mobileRegex = /^1[3-9]\d{9}$/;
  // 座机号正则：区号可选，号码至少7位，支持多种分隔符
  const landlineRegex = /^((0\d{2,3})[- ]?)?(\d{7,8})$/;

  if (mobileRegex.test(value) || landlineRegex.test(value)) {
    callback();
  } else {
    callback(new Error('请输入有效的手机号或座机号'));
  }
};

/**
 * 验证统一社会信用代码
 * @param value 统一社会信用代码
 * @returns { isValid: boolean, message?: string } 验证结果
 */
export const validateSocityCode = (value: string): { isValid: boolean; message?: string } => {
  if (!value) {
    return { isValid: false, message: '统一社会信用代码不能为空' };
  }

  // 去除空格并转为大写
  const code = value.trim().toUpperCase();

  // 统一社会信用代码必须是18位
  if (code.length !== 18) {
    return { isValid: false, message: '统一社会信用代码必须是18位字符' };
  }

  // 统一社会信用代码由数字和大写字母组成，不包含I、O、S、V、Z
  const validCharsRegex = /^[0-9A-HJ-NP-RT-UW-Y]{18}$/;
  if (!validCharsRegex.test(code)) {
    return { isValid: false, message: '统一社会信用代码格式不正确，不能包含I、O、S、V、Z等字符' };
  }

  // 校验码验证
  // 统一社会信用代码的校验码计算规则
  const weights = [1, 3, 9, 27, 19, 26, 16, 17, 20, 29, 25, 13, 8, 24, 10, 30, 28];
  const charMap: Record<string, number> = {
    '0': 0,
    '1': 1,
    '2': 2,
    '3': 3,
    '4': 4,
    '5': 5,
    '6': 6,
    '7': 7,
    '8': 8,
    '9': 9,
    A: 10,
    B: 11,
    C: 12,
    D: 13,
    E: 14,
    F: 15,
    G: 16,
    H: 17,
    J: 18,
    K: 19,
    L: 20,
    M: 21,
    N: 22,
    P: 23,
    Q: 24,
    R: 25,
    T: 26,
    U: 27,
    W: 28,
    X: 29,
    Y: 30,
  };

  let sum = 0;
  for (let i = 0; i < 17; i++) {
    const char = code[i];
    const num = charMap[char];
    if (num === undefined) {
      return { isValid: false, message: '统一社会信用代码包含非法字符' };
    }
    sum += num * weights[i];
  }

  const checkCodeMap: Record<number, string> = {
    0: '0',
    1: '1',
    2: '2',
    3: '3',
    4: '4',
    5: '5',
    6: '6',
    7: '7',
    8: '8',
    9: '9',
    10: 'A',
    11: 'B',
    12: 'C',
    13: 'D',
    14: 'E',
    15: 'F',
    16: 'G',
    17: 'H',
    18: 'J',
    19: 'K',
    20: 'L',
    21: 'M',
    22: 'N',
    23: 'P',
    24: 'Q',
    25: 'R',
    26: 'T',
    27: 'U',
    28: 'W',
    29: 'X',
    30: 'Y',
  };

  const remainder = sum % 31;
  const expectedCheckCode = checkCodeMap[31 - remainder] || '0';
  const actualCheckCode = code[17];

  if (expectedCheckCode !== actualCheckCode) {
    return { isValid: false, message: '统一社会信用代码校验码不正确' };
  }

  return { isValid: true };
};

/**
 * 验证开票电话
 * @param value 开票电话
 * @returns { isValid: boolean, message?: string } 验证结果
 */
export const validateInvoiceTel = (value: string): { isValid: boolean; message?: string } => {
  if (!value) {
    return { isValid: false, message: '开票电话不能为空' };
  }

  const tel = value.trim();

  // 手机号正则：1开头，后面跟10位数字
  const mobileRegex = /^1[3-9]\d{9}$/;
  // 座机号正则：区号可选，号码至少7位，支持多种分隔符（与 validatePhone 保持一致）
  const landlineRegex = /^((0\d{2,3})[- ]?)?(\d{7,8})$/;

  if (mobileRegex.test(tel) || landlineRegex.test(tel)) {
    return { isValid: true };
  } else {
    return { isValid: false, message: '开票电话格式不正确，请输入有效的手机号或座机号' };
  }
};

/**
 * 处理长数据
 * @param address
 * @returns
 */
export const getAddressLabel = (address: string) => {
  if (!address) {
    return '-';
  }
  const addressArr = address.split(';');
  if (addressArr?.length > 1) {
    return addressArr[0] ? addressArr[0] + '...' : '-';
  } else if (addressArr?.length === 1) {
    return addressArr[0] || '-';
  }
  return '-';
};

/**
 * 将文件 URL 编码为可安全传输的 Base64 字符串
 * @param url 需要编码的文件 URL
 * @returns 编码后的字符串（已 encodeURIComponent）
 */
export function encodeFileUrl(url: string): string {
  if (!url) return '';
  try {
    return encodeURIComponent(btoa(unescape(encodeURIComponent(url))));
  } catch (err) {
    console.error('[encodeFileUrl] URL 编码失败:', err);
    return '';
  }
}

/**
 * 构造文件在线预览地址
 * @param fileUrl 文件相对路径，例如 "/upload/test.pdf"
 * @param options 可选项：自定义预览服务地址与文件基础路径
 */
export function buildPreviewUrl(
  fileUrl: string,
  options?: {
    previewHost?: string; // 预览服务地址
    baseFileHost?: string; // 文件基础路径（如 import.meta.env.ROSA_MINIO_FILE）
  },
): string {
  const {
    previewHost = 'http://114.217.150.20:18860/onlinePreview',
    baseFileHost = import.meta.env.ROSA_MINIO_FILE || '',
  } = options || {};

  if (!fileUrl) {
    console.warn('[buildPreviewUrl] fileUrl 为空');
    return '';
  }

  const fullUrl = `${baseFileHost}${fileUrl}`;
  const encoded = encodeFileUrl(fullUrl);

  return `${previewHost}?url=${encoded}`;
}

// 响应拦截器直接下载文件
export const directDownloadFn = (res: any) => {
  const disposition = res.headers['content-disposition'];
  let filename = '';

  if (disposition) {
    // 优先匹配 filename*=UTF-8''... 这种格式
    const utf8Match = disposition.match(/filename\*=(?:UTF-8'')?([^;]+)/);
    const asciiMatch = disposition.match(/filename="?([^"]+)"?/);
    if (utf8Match && utf8Match[1]) {
      filename = decodeURIComponent(utf8Match[1]);
    } else if (asciiMatch && asciiMatch[1]) {
      filename = asciiMatch[1];
    }
  }

  // 检查文件名是否合法
  // 检查文件名合法性
  // 规则：不为空、长度不过长、不包含非法字符
  const isValidFilename = (name: string) => {
    // 禁止的字符: \ / : * ? " < > | 以及控制字符
    const illegalRe = /[\\/:*?"<>|\x00-\x1F]/g;
    return typeof name === 'string' && name.trim().length > 0 && name.length < 255 && !illegalRe.test(name);
  };

  if (!isValidFilename(filename)) {
    console.warn('⚠️ 文件名不合法，取消下载:', filename);
    return; // 直接中止下载逻辑
  }

  // 3️⃣ 创建 blob 对象
  const blob = new Blob([res.data], { type: res.headers['content-type'] });

  // 4️⃣ 创建 URL 对象
  const blobUrl = URL.createObjectURL(blob);

  // 5️⃣ 创建 a 标签并模拟点击下载
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();

  // 6️⃣ 清理资源
  URL.revokeObjectURL(blobUrl);
  document.body.removeChild(a);
};

/**
 * 处理楼宇别名：
 * 当楼栋号中带 "X" 或 "x" 时，将房间名称上传到 buildingName 字段；
 * 否则上传到 spaceName 字段。
 * @param data 房间数据
 * @returns 处理后的数据
 */
export function handleBuildingName(data: any) {
  const dataTemp = cloneDeep(data);
  const buildingNumber = dataTemp.buildingNumber ?? '';

  if (/x/i.test(buildingNumber)) {
    // 显式设置为空字符串，避免 undefined
    dataTemp.buildingNo = '';
  } else {
    dataTemp.buildingNo = cloneDeep(dataTemp.buildingName) ?? '';
    dataTemp.buildingName = '';
  }
  return dataTemp;
}

/**
 * 处理房间别名：
 * 当房间号中带 "X" 或 "x" 时，将房间名称上传到 roomNameAlias 字段；
 * 否则上传到 spaceName 字段。
 * @param data 房间数据
 * @returns 处理后的数据
 */
export function handleRoomName(data: any) {
  const dataTemp = cloneDeep(data);
  const roomNumber = dataTemp.roomNumber ?? '';

  if (/x/i.test(roomNumber)) {
    // 确保 roomNameAlias 有值，避免 undefined
    dataTemp.roomNameAlias = cloneDeep(dataTemp.spaceName) ?? '';
    dataTemp.spaceName = '';
  } else {
    // 显式设置为空字符串，避免 undefined
    dataTemp.roomNameAlias = '';
  }
  return dataTemp;
}

/**
 *动态引入资源
 * @param url
 * @returns
 */
export const getAssetsUrl = (url: string) => {
  let path = new URL(`../assets/${url}`, import.meta.url);
  path = path.href.indexOf('undefined') === -1 ? path : new URL(``, import.meta.url);
  return path.href;
};

/** 身份证号脱敏 */
export function maskIDCard(IDCard: string, left = 5, right = 4) {
  if (IDCard?.length !== 18) return IDCard;
  return IDCard?.slice(0, left) + '*'.repeat(IDCard?.length - left - right) + IDCard?.slice(IDCard?.length - right);
}

// 身份证的校验
export const checkIDCard = (_rule: any, value: any, callback: any) => {
  const IDCardReg = /^\d{6}(18|19|20)\d{2}(0[1-9]|1[0-2])(([0-2][1-9])|10|20|30|31)\d{3}(\d|X|x)$/;
  if (value) {
    if (IDCardReg.test(value)) {
      callback();
    } else {
      callback(new Error('身份证号格式不正确'));
    }
  } else {
    callback(new Error('请输入身份证号'));
  }
};
// 护照
export const checkPassport = (_rule: any, value: any, callback: any) => {
  /**
   * 规则： 新版8位或18位数字， 旧版10位数字 + 英文字母
   * 样本： 12345678 或 1234567890B
   */
  const passportReg = /^\d{8}|^[a-zA-Z0-9]{10}|^\d{18}$/;
  if (value) {
    if (passportReg.test(value)) {
      callback();
    } else {
      callback(new Error('护照号码格式不正确'));
    }
  } else {
    callback(new Error('请输入护照号码'));
  }
};
// 港澳通行证
export const checkHKCard = (_rule: any, value: any, callback: any) => {
  /**
   * 规则： H/M + 10位或6位数字
   * 样本： H1234567890
   */
  const HKCardReg = /^([A-Z]\d{6,10}(\(\w{1}\))?)$/;
  if (value) {
    if (HKCardReg.test(value)) {
      callback();
    } else {
      callback(new Error('港澳通行证号格式不正确'));
    }
  } else {
    callback(new Error('请输入港澳通行证号'));
  }
};
// 台湾通行证
export const checkTWCard = (_rule: any, value: any, callback: any) => {
  /**
   * 规则： 新版8位或18位数字， 旧版10位数字 + 英文字母
   * 样本： 12345678 或 1234567890B
   */
  const TWCardReg = /^\d{8}|^[a-zA-Z0-9]{10}|^\d{18}$/;
  if (value) {
    if (TWCardReg.test(value)) {
      callback();
    } else {
      callback(new Error('台湾通行证号格式不正确'));
    }
  } else {
    callback(new Error('请输入台湾通行证号'));
  }
};

/**
 * 根据经纬度数据判断 GEO 类型,并返回类型和数据
 * @param clongitudeLatitude 经纬度数据（string | any）
 */
export const getGeoType = (
  clongitudeLatitude?: string | unknown,
):
  | {
      type: GeoTypeEnum | undefined;
      coordinates: number[];
    }
  | undefined => {
  if (!clongitudeLatitude) return;

  let data: any;

  // 字符串先解析
  if (typeof clongitudeLatitude === 'string') {
    try {
      data = JSON.parse(clongitudeLatitude);
    } catch {
      return;
    }
  } else {
    data = clongitudeLatitude;
  }

  if (!Array.isArray(data)) return;

  // Point: [lng, lat]
  if (data.length === 2 && typeof data[0] === 'number' && typeof data[1] === 'number') {
    return {
      type: GeoTypeEnum.marker,
      coordinates: data,
    };
  }

  // Point: [[lng, lat]]

  if (Array.isArray(data[0]) && data.length === 1 && typeof data[0][0] === 'number' && typeof data[0][1] === 'number') {
    return {
      type: GeoTypeEnum.marker,
      coordinates: data[0],
    };
  }

  // Polygon: [[lng, lat], [lng, lat], ...]
  if (Array.isArray(data[0]) && data.length > 2 && typeof data[0][0] === 'number' && typeof data[0][1] === 'number') {
    return {
      type: GeoTypeEnum.polygon,
      coordinates: data,
    };
  }

  return;
};

/**
 * 判断是否可以开启多边形 3D 同步效果
 * @returns
 */
export const canEnablePolygon3DSync = () => {
  const canvas = document.createElement('canvas');
  const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

  if (!gl) {
    console.log('当前设备不建议开启3D效果');
    return false;
  }

  const info = {
    maxTextureSize: (gl as any).getParameter((gl as any).MAX_TEXTURE_SIZE),
    hardwareConcurrency: navigator.hardwareConcurrency,
  };

  console.log('当前设备详情：', info);

  if (isNil(info.hardwareConcurrency) || isNil(info.maxTextureSize)) {
    console.log('未获取指标数据，不开启3D效果');
    return false;
  }

  /** 显存侧指标 */
  if (info.maxTextureSize <= 8192) {
    console.log('当前设备显存侧指标太小，不建议开启3D效果');
    return false;
  }

  /** CPU 核心数（兜底） */
  if (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 6) {
    console.log('当前设备CPU核心数过，小不建议开启3D效果');
    return false;
  }

  console.log('当前设备可以开启3D效果');
  return true;
};

/**
 * 根据文件后缀返回对应图标name
 * @param suffix
 */
export function getIconNameBySuffix(suffix: string) {
  /**
   * @description 文件类型图标
   * */
  const fileTypeMap: Record<string, string> = {
    pdf: 'pdf',
    mp4: 'mp4',
    jpeg: 'jpeg',
    jpg: 'jpg',
    txt: 'txt',
    docx: 'docx',
    png: 'png',
    xlsx: 'xlsx',
    rar: 'compress',
    zip: 'compress',
    doc: 'docx',
    xls: 'xlsx',
  };
  const defaultType = 'other';
  return fileTypeMap[lowerCase(suffix)]
    ? `common_icon_attachment_${fileTypeMap[lowerCase(suffix)]}`
    : `common_icon_attachment_${defaultType}`;
}

/** 根据产权人id获取产权人所在部门的id */
export async function getOwnerDeptIdByOwnerId(ownerId: string) {
  const res = await getDeptList({ clientId: ownerId });
  const list = res?.data || [];
  const dept = list?.length ? list[0] : {};
  return { ownerDeptId: dept?.deptId || null, ownerDeptAncestors: dept?.ancestors || null };
}

/** 根据公司id获取公司所绑的产权人的id */
export async function getOwnerIdByDeptId(companyId: string) {
  const res = await getDeptList({ deptId: companyId });
  const list = res?.data || [];
  const dept = list?.length ? list[0] : {};
  return { ownerId: dept?.clientId || null };
}

/** 根据部门id获取部门所有上级id */
export async function getDeptAncestorsByDeptId(deptId: string) {
  const res = await getDeptList({ deptId });
  const list = res?.data || [];
  const dept = list?.length ? list[0] : {};
  return dept?.ancestors;
}
